import { SceneManager } from '../../../../visualization/sceneManager'
import {
  AbstractMesh,
  BoundingInfo,
  TmpVectors,
  Vector3,
} from '@babylonjs/core'
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { MeasurementsConfirmIcon, MeasurementsEditIcon } from '../../../Svgs'
import { computeBoundingBox } from '../../../../visualization/utils'

const numberRegex = /^\d+[.]?\d*$/

interface BoxMeasurementBaseProps {
  mesh: AbstractMesh
  dimension: 'x' | 'y' | 'z'
  isVisible: boolean
  units?: 'FT'
}

interface ReadonlyBoxMeasurementProps extends BoxMeasurementBaseProps {
  isEditable?: false
}

interface EditableBoxMeasurementProps extends BoxMeasurementBaseProps {
  isEditable?: true
  onChange: (value: number) => void
  onConfirm: () => void
  onDismiss: () => void
}

type BoxMeasurementProps =
  | ReadonlyBoxMeasurementProps
  | EditableBoxMeasurementProps

const BoxMeasurement = ({
  mesh,
  dimension,
  isVisible,
  units = 'FT',
  ...props
}: BoxMeasurementProps) => {
  const boxRef = useRef<HTMLDivElement>(null)
  const [isEditMode, setIsEditMode] = useState<boolean>(false)

  useEffect(() => {
    const scene = SceneManager.getInstance().getScene()
    const engine = SceneManager.getInstance().getEngine()
    const camera = SceneManager.getInstance().getCamera()
    if (!scene || !camera || !engine) return

    const measurement3Dposition = new Vector3()
    const measurement2Dposition = new Vector3()
    const updateMeasurementPositionCallback = () => {
      const box = boxRef.current
      if (!box) {
        return
      }

      let boundingBox
      if (mesh.geometry) {
        boundingBox = mesh.getBoundingInfo().boundingBox
      } else {
        const bbox = computeBoundingBox(mesh.getChildMeshes(), true)
        boundingBox = new BoundingInfo(
          bbox.min,
          bbox.max,
          mesh.getWorldMatrix(),
        ).boundingBox
      }

      const boxCenterToCamera = camera.globalPosition.subtractToRef(
        boundingBox.centerWorld,
        TmpVectors.Vector3[0],
      )
      const isFacingToCameraX =
        Vector3.Dot(boundingBox.directions[0], boxCenterToCamera) > 0
      const isFacingToCameraZ =
        Vector3.Dot(boundingBox.directions[2], boxCenterToCamera) > 0

      measurement3Dposition.copyFrom(boundingBox.center)

      switch (dimension) {
        case 'x':
          measurement3Dposition.y += boundingBox.extendSize.y
          measurement3Dposition.z += isFacingToCameraZ
            ? boundingBox.extendSize.z
            : -boundingBox.extendSize.z
          break
        case 'y':
          measurement3Dposition.x += isFacingToCameraX
            ? boundingBox.extendSize.x
            : -boundingBox.extendSize.x
          measurement3Dposition.z += isFacingToCameraZ
            ? boundingBox.extendSize.z
            : -boundingBox.extendSize.z
          break
        case 'z':
          measurement3Dposition.x += isFacingToCameraX
            ? boundingBox.extendSize.x
            : -boundingBox.extendSize.x
          measurement3Dposition.y += -boundingBox.extendSize.y
          measurement3Dposition.z += isFacingToCameraZ
            ? -boundingBox.extendSize.z
            : boundingBox.extendSize.z
          break
      }

      Vector3.ProjectToRef(
        measurement3Dposition,
        boundingBox.getWorldMatrix(),
        scene.getTransformMatrix(),
        camera.viewport.toGlobal(
          engine.getRenderWidth(true),
          engine.getRenderHeight(true),
        ),
        measurement2Dposition,
      )

      const appbarHeight =
        document.getElementsByTagName('header').item(0)?.offsetHeight ?? 52
      const boxWidth = box.offsetWidth
      const boxHeight = box.offsetHeight

      switch (dimension) {
        case 'x':
          box.style.left = `${measurement2Dposition.x - boxWidth / 2}px`
          box.style.top = `${measurement2Dposition.y + appbarHeight}px`
          break
        case 'y':
          box.style.left = `${measurement2Dposition.x}px`
          box.style.top = `${measurement2Dposition.y + appbarHeight - boxHeight / 2}px`
          break
        case 'z':
          box.style.left = `${measurement2Dposition.x}px`
          box.style.top = `${measurement2Dposition.y + appbarHeight - boxHeight / 2}px`
          break
      }

      setValue(
        String(mesh.scaling[dimension] * boundingBox.extendSize[dimension] * 2),
      )
    }

    updateMeasurementPositionCallback()

    const onAfterWorldMatrixUpdateObserver =
      mesh.onAfterWorldMatrixUpdateObservable.add(
        updateMeasurementPositionCallback,
      )

    const viewMatrixChangedObserver = camera.onViewMatrixChangedObservable.add(
      updateMeasurementPositionCallback,
    )

    const projectionMatrixChangedObserver =
      camera.onProjectionMatrixChangedObservable.add(
        updateMeasurementPositionCallback,
      )

    const onResizeObserver = engine.onResizeObservable.add(
      updateMeasurementPositionCallback,
    )

    return () => {
      onAfterWorldMatrixUpdateObserver.remove()
      viewMatrixChangedObserver.remove()
      projectionMatrixChangedObserver.remove()
      onResizeObserver.remove()

      setIsEditMode(false)
    }
  }, [mesh, dimension])

  const boundingBox = mesh.getBoundingInfo().boundingBox
  const [value, setValue] = useState<string>(
    String(boundingBox.extendSize[dimension] * 2),
  )
  const displayValue = useMemo(() => {
    const parsedValue = parseFloat(value)
    return isEditMode || isNaN(parsedValue) ? value : parsedValue.toFixed(2)
  }, [value, isEditMode])

  const handleEditTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!props.isEditable) {
      return
    }

    const { onChange } = props

    const originalValue = event.target.value
    const valueToTest = originalValue || '0'
    const parsedValue = parseFloat(valueToTest)

    if (!isNaN(parsedValue) && numberRegex.test(valueToTest)) {
      setValue(originalValue)
      onChange(parsedValue)
    }
  }

  const handleEditTextFieldKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!props.isEditable || !isEditMode) {
      return
    }

    const { onConfirm, onDismiss } = props

    if (event.key === 'Enter') {
      onConfirm()
    } else if (event.key === 'Escape') {
      onDismiss()
    } else {
      return
    }

    const boundingBox = mesh.getBoundingInfo().boundingBox
    setValue(String(boundingBox.extendSize[dimension] * 2))
    setIsEditMode(false)
  }

  const handleEditButtonClick = () => {
    if (!props.isEditable) {
      return
    }

    const { onConfirm } = props

    if (isEditMode) {
      onConfirm()
      const boundingBox = mesh.getBoundingInfo().boundingBox
      setValue(String(boundingBox.extendSize[dimension] * 2))
    }

    setIsEditMode((prev) => !prev)
  }

  return (
    <Box
      ref={boxRef}
      sx={{
        borderRadius: '6px',
        border: '1px solid rgba(255, 255, 255, 0.47)',
        opacity: isVisible ? 0.9 : 0,
        background: 'rgba(4, 0, 46, 0.90)',
        backgroundBlendMode: 'multiply',
        boxShadow: '0px 1px 1px 0px rgba(0, 0, 0, 0.25)',
        backdropFilter: 'blur(2px)',
        width: 'fit-content',
        height: 'fit-content',
        pointerEvents: 'all',
        position: 'fixed',
        display: isVisible ? 'flex' : 'none',
        flexDirection: 'row',
        alignItems: 'center',
        maxWidth: '128px',
      }}
    >
      {props.isEditable ? (
        <TextField
          size='small'
          autoComplete='off'
          value={displayValue}
          disabled={!isEditMode}
          onChange={handleEditTextFieldChange}
          onKeyUp={handleEditTextFieldKeyUp}
          sx={{
            padding: '2px',
            border: 'none',
            '& fieldset': {
              border: 'none',
            },
            '& .MuiInputBase-input': {
              maxWidth: '56px',
              padding: '2px 0px 2px 2px',
              '&.Mui-disabled': {
                color: '#F9F9F9',
                WebkitTextFillColor: '#F9F9F9',
              },
            },
            '& .MuiInputBase-root': {
              padding: 0,
              fontFamily: 'Inter',
              fontSize: '1rem',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: 'normal',
              background: '#F9F9F9',
              color: '#04002E',
              '&.Mui-disabled': {
                background: 'transparent',
                color: '#F9F9F9',
                '& .MuiInputAdornment-root .MuiTypography-root': {
                  color: '#F9F9F9',
                },
              },
              '& .MuiInputAdornment-root': {
                margin: 0,
                paddingRight: '2px',
                '& .MuiTypography-root': {
                  color: '#06004A',
                },
              },
            },
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position='end'>{units}</InputAdornment>
              ),
            },
          }}
        />
      ) : (
        <Box
          sx={{
            padding: '4px',
            display: 'flex',
            gap: '4px',
            '& .MuiTypography-root': {
              color: '#F9F9F9',
              fontFamily: 'Inter',
              fontSize: '1rem',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: 'normal',
            },
          }}
        >
          <Typography>{displayValue}</Typography>

          <Typography>{units}</Typography>
        </Box>
      )}

      {props.isEditable && (
        <>
          <Divider
            flexItem
            orientation='vertical'
            sx={{
              my: '2px',
              backgroundColor: '#514F72',
            }}
          />

          <IconButton
            sx={{
              padding: '4px',
            }}
            onClick={handleEditButtonClick}
          >
            {isEditMode ? (
              <MeasurementsConfirmIcon
                sx={{
                  color: 'white',
                  fontSize: '1rem',
                }}
              />
            ) : (
              <MeasurementsEditIcon
                sx={{
                  color: 'white',
                  fontSize: '1rem',
                }}
              />
            )}
          </IconButton>
        </>
      )}
    </Box>
  )
}

export default BoxMeasurement
